/**
 * <cart-drawer>: opens after any add to cart (event `cart:item-added`) or a tap
 * on the header cart icon; quantity changes go through /cart/change.js and the
 * drawer re-renders from the Section Rendering API (`sections` param).
 */
(() => {
  if (customElements.get('cart-drawer')) return;

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-cart-dialog]');
      this.sectionId = this.dataset.sectionId;
      this.busy = false;

      this.addEventListener('click', (event) => this.onClick(event));
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      document.addEventListener('cart:item-added', (event) => this.onItemAdded(event));

      // Header cart icon opens the drawer instead of navigating to /cart
      document.querySelectorAll('[data-cart-link]').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.open();
        });
      });
    }

    open() {
      if (!this.dialog.open) this.dialog.showModal();
    }

    close() {
      this.dialog.close();
    }

    async onItemAdded(event) {
      const html = event.detail && event.detail.sections && event.detail.sections[this.sectionId];
      if (html) {
        this.render(html);
      } else {
        await this.refresh();
      }
      this.open();
    }

    onClick(event) {
      if (event.target.closest('[data-cart-close]')) {
        this.close();
        return;
      }
      const qtyButton = event.target.closest('[data-qty-change]');
      if (qtyButton) {
        const line = qtyButton.closest('[data-line]').dataset.line;
        this.changeQuantity(Number(line), Number(qtyButton.dataset.qtyChange));
      }
    }

    async changeQuantity(line, quantity) {
      if (this.busy) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(this.dataset.cartChangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: [this.sectionId] }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message);
        this.render(result.sections[this.sectionId]);
        this.announce();
      } catch (error) {
        const errorEl = this.querySelector('[data-cart-error]');
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
      }
    }

    async refresh() {
      const url = `${window.location.pathname}?sections=${this.sectionId}`;
      const data = await (await fetch(url)).json();
      this.render(data[this.sectionId]);
    }

    /** Swap the drawer contents with freshly rendered markup, keep the dialog open. */
    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-content]');
      const current = this.querySelector('[data-cart-content]');
      if (!fresh || !current) return;
      const scroll = current.querySelector('.cart-drawer__items')?.scrollTop || 0;
      current.replaceWith(fresh);
      const items = fresh.querySelector('.cart-drawer__items');
      if (items) items.scrollTop = scroll;

      const count = Number(fresh.dataset.count);
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    }

    announce() {
      const el = this.querySelector('[data-cart-announce]');
      const count = this.querySelector('[data-cart-content]').dataset.count;
      el.textContent = '';
      window.setTimeout(() => (el.textContent = `${count}`), 50);
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
